import React from 'react'
import { useTranslation } from 'react-i18next'
import TableHeaders from '../../Interfaces/TableHeaders'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Registrant } from '../../../swagger'
import { Box } from '@mui/system'
import { getCommaSeparatedList } from '../../../utils/getCommaSeparatedList'
import { TableHeaderForPDF } from '../../Table/TableHeaderForPDF'
import { styles } from './styles'
import { Event } from '../../../swagger/models/Event'
import {
  dateToDashString,
  formatFullDateString,
} from '../../../utils/dateUtility'

const widthForName = '9%'
const widthForEmail = '18%'
const widthForPhone = '14%'
const widthForAttending = '9%'
const widthForRoles = '26%'
const widthForDateRegistered = '14%'

const RegistrantsPDF: React.FC<{ registrants: Registrant[]; event: Event }> = ({
  registrants,
  event,
}) => {
  const { t } = useTranslation()

  const tableHeaders: TableHeaders[] = [
    {
      label: t('RegistrantsPDf.Table.Header.FirstName', 'First Name'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForName },
    },
    {
      label: t('RegistrantsPDf.Table.Header.LastName', 'Last Name'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForName },
    },
    {
      label: t('RegistrantsPDf.Table.Header.Roles', 'Roles'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForRoles },
    },
    {
      label: t('RegistrantsPDf.Table.Header.Email', 'Email'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForEmail },
    },
    {
      label: t('RegistrantsPDf.Table.Header.PhoneNumber', 'Phone Number'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForPhone },
    },
    {
      label: t('RegistrantsPDf.Table.Header.DateRegistered', 'Date Registered'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForDateRegistered },
    },
    {
      label: t('RegistrantsPDf.Table.Header.Attending', 'Attending'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForAttending },
    },
    {
      label: t('RegistrantsPDf.Table.Header.Status', 'Status'),
      align: 'left',
      cssProps: { ...styles.tableHeaderCell, width: widthForAttending },
    },
  ]

  const buildAddressInformation = () => {
    const { city = '', state = '', zip = '' } = event.address || {}
    return [city, state, zip].filter(Boolean)
  }

  const isSameDate =
    event.endDate && event.startDate.getTime() === event.endDate.getTime()

  return (
    <Box>
      <div style={styles.eventInfo}>
        <Typography
          style={styles.title}
          variant="subtitle1"
          component="h5"
          justifyContent={'flex-start'}
        >
          {t(
            'RegistrantsPDf.Section.EventInformation',
            'Event Registrants - {{eventInfo}}',
            {
              eventInfo: `${event.eventType} - ${event.name}`,
            }
          )}
        </Typography>

        <div style={{ fontSize: '9px', left: 6, paddingBottom: 6 }}>
          {formatFullDateString(event.startDate)} {event.startTime} -{' '}
          {isSameDate || !event.endDate
            ? ''
            : formatFullDateString(event.endDate)}{' '}
          {event.endTime} - {event.timezone}
        </div>

        <div style={{ fontSize: '9px', left: 6 }}>
          {t(
            'RegistrantsPDf.Section.AddressInformation',
            'Registration required. Details to come, {{addressInfo}}',
            {
              addressInfo: getCommaSeparatedList(
                buildAddressInformation()
              ).replace(' y ', ' , '),
            }
          )}
        </div>
      </div>
      <Box sx={styles.containerTable}>
        <Table
          aria-labelledby={'registrants-table-for-pdf'}
          style={styles.table}
        >
          <TableHead>
            <TableHeaderForPDF tableHeaders={tableHeaders} />
          </TableHead>
          <TableBody>
            {registrants.map((row) => (
              <TableRow key={row.id}>
                <TableCell
                  style={{
                    ...styles.tableBodyCell,
                    width: widthForName,
                  }}
                >
                  {row.firstName}
                </TableCell>
                <TableCell
                  style={{
                    ...styles.tableBodyCell,
                    width: widthForName,
                  }}
                >
                  {row.lastName}
                </TableCell>
                <TableCell
                  style={{
                    ...styles.tableBodyCell,
                    width: widthForRoles,
                  }}
                >
                  {row.roles && row.roles.length > 0
                    ? row.roles && row.roles.length > 1
                      ? getCommaSeparatedList(row.roles)
                      : row.roles
                    : t('RegistrantsPDF.Table.Cell.NoRole', '')}
                </TableCell>
                <TableCell
                  style={{
                    ...styles.tableBodyCell,
                    width: widthForEmail,
                  }}
                >
                  {row.email}
                </TableCell>
                <TableCell
                  style={{
                    ...styles.tableBodyCell,
                    width: widthForPhone,
                  }}
                >
                  {row.phone}
                </TableCell>
                <TableCell
                  style={{
                    ...styles.tableBodyCell,
                    width: widthForDateRegistered,
                  }}
                >
                  {row.createdDate ? dateToDashString(row.createdDate) : ''}
                </TableCell>
                <TableCell
                  style={{
                    ...styles.tableBodyCell,
                    width: widthForAttending,
                  }}
                >
                  {row.attending ? 'Yes' : 'No'}
                </TableCell>
                <TableCell
                  style={{
                    ...styles.tableBodyCell,
                    width: widthForAttending,
                  }}
                >
                  {row.isMember ? 'Member' : 'None'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}

export default RegistrantsPDF
